import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import './negativeReviews.css';
import BiWeeklyPlatformStats from '../biweeklyPlatformStats/biweeklyPlatformStats';

const starIcons = (rating) => {
  return Array(5)
    .fill()
    .map((_, i) => (
      <span key={i} className={i < rating ? 'star-filled' : 'star-empty'}>
        ★
      </span>
    ));
};

const NegativeReviews = () => {
  const { reviews } = useSelector((state) => state.reviews);
  const [selectedProductCode, setSelectedProductCode] = useState('all');
  const [selectedRating, setSelectedRating] = useState('all');
  const { search } = useLocation();

  const today = new Date();
  const twoWeeksAgo = new Date(today);
  twoWeeksAgo.setDate(today.getDate() - 14);

  // 🔹 Filter to recent negative reviews
  const recentNegativeReviews = useMemo(() => {
    return reviews
      .filter((review) => {
        const reviewDate = new Date(review[3].split('/').reverse().join('-')); // DD/MM/YYYY
        return review[4] <= 3 && reviewDate >= twoWeeksAgo; // 3-star or below
      })
      .sort((a, b) => {
        const dateA = new Date(a[3].split('/').reverse().join('-'));
        const dateB = new Date(b[3].split('/').reverse().join('-'));
        return dateB - dateA; // newest first
      });
  }, [reviews]);

  // Unique product codes
  const productCodes = useMemo(() => {
    const codes = recentNegativeReviews.map((r) => r[6]?.trim()).filter(Boolean);
    return ['all', ...new Set(codes)];
  }, [recentNegativeReviews]);

  // Rating filter options (1,2,3)
  const ratingOptions = useMemo(() => {
    const ratings = recentNegativeReviews.map((r) => r[4]);
    return ['all', ...new Set(ratings.filter((r) => [1, 2, 3].includes(r)))];
  }, [recentNegativeReviews]);

  // Apply both filters
  const filteredReviews = useMemo(() => {
    return recentNegativeReviews.filter(
      (review) =>
        (selectedProductCode === 'all' || review[6] === selectedProductCode) &&
        (selectedRating === 'all' || review[4] === Number(selectedRating))
    );
  }, [recentNegativeReviews, selectedProductCode, selectedRating]);

  return (
    <div className="negativeReviews">
      <h3>Bi-Weekly Data</h3>
      <div>
        <BiWeeklyPlatformStats />
      </div>

      <div>
        <h4 className="heading2">Recent Negative Reviews</h4>
        <div className='filter-wrapper'>
        <div className="filter-container">
          <label htmlFor="ratingFilter">Rating: </label>
          <select
            id="ratingFilter"
            value={selectedRating}
            onChange={(e) => setSelectedRating(e.target.value)}
          >
            {ratingOptions
              .sort((a, b) => (a === 'all' ? -1 : a - b)) 
              .map((rating) => (
                <option key={rating} value={rating} className="options">
                  {rating === 'all' ? 'All Ratings' : `${rating} Star`}
                </option>
              ))}
          </select>
        </div>

        <div className="filter-container">
          <label htmlFor="productFilter">Product Code: </label>
          <select
            id="productFilter"
            value={selectedProductCode}
            onChange={(e) => setSelectedProductCode(e.target.value)}
          >
            {productCodes.map((code) => (
              <option key={code} value={code} className="options">
                {code === 'all' ? 'All Products' : code}
              </option>
            ))}
          </select>
        </div>
        </div>
        {filteredReviews.length > 0 ? (
          filteredReviews.map((review, index) => (
            <div key={index} className="review-card">
              <div className="review-header">
                <div className="review-id-conatiner">
                  <span className="review-id">{review[1]}</span>
                  <span className="review-date">Tour Date: {review[2]}</span>
                </div>
                <div className="review-star-conatiner">
                  <span className="stars">{starIcons(review[4])}</span>
                  <span className="product-codes">{review[6]}</span>
                </div>
              </div>
              <p className="comment">{review[5]}</p>
              <div className="review-footer">
                <span className="date">Comment Date: {review[3]}</span>
                <span className="guide-name">
                  <Link
                    to={{ pathname: `/guides/${encodeURIComponent(String(review[10] ?? ''))}`, search }}
                    className="guide-link"
                  >
                    {review[10]}
                  </Link>
                </span>
              </div>
            </div>
          ))
        ) : (
          <p>No negative reviews found in the last two weeks.</p>
        )}
      </div>
    </div>
  );
};

export default NegativeReviews;
